import { useCallback } from 'react';
import { useQueryClient, useMutation } from 'react-query';
import { FormsQueryKeys, FormsWritebacks } from '@frontend/api-forms';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { useFormsSubmissionsV2Context, useFormsSubmissionsDetailsV2Context } from '../../../../../../../provider';
import { useWritebackStore } from '../../provider';
import { useWritebackError } from './use-writeback-error';

type TriggerWritebackRetryFn = (retrySearch?: boolean) => Promise<FormsWritebacks.Types.RetryWritebackResponse>;

interface UseWritebackRetryResults {
  triggerWritebackRetry: TriggerWritebackRetryFn;
}

export const useWritebackRetry = (): UseWritebackRetryResults => {
  const { t } = useTranslation('forms');
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { locationId = '', submissionId = '' } = useFormsSubmissionsV2Context();
  const { submissionDetailsQueryKey } = useFormsSubmissionsDetailsV2Context();
  const { getWBErrorMessage, defaultErrorMessage } = useWritebackError();
  const { getDataForWriteback, setIsRetryingWriteback } = useWritebackStore([
    'getDataForWriteback',
    'setIsRetryingWriteback',
  ]);

  const refetchSubmission = useCallback(() => {
    queryClient.invalidateQueries(FormsQueryKeys.formSubmissions);
    queryClient.invalidateQueries(submissionDetailsQueryKey);
  }, [queryClient, submissionDetailsQueryKey]);

  // TODO: Add Schema query for this.
  const { mutateAsync: retryWriteback } = useMutation(FormsWritebacks.API.retryWriteback, {
    onSuccess: (res) => {
      refetchSubmission();
      if (!res.success) {
        alert.error(
          getWBErrorMessage({
            errorString: res.data?.data?.message,
            separator: '|',
          })
        );
        return;
      }

      alert.success(t('Writeback was successful!'));
    },
    onError: () => {
      refetchSubmission();
      alert.error(defaultErrorMessage);
    },
  });

  const triggerWritebackRetry = useCallback<TriggerWritebackRetryFn>(
    async (retrySearch = false) => {
      const settingIdsToRetry: string[] = [];
      const {
        createPersonSetting,
        updatePersonSetting,
        uploadDocumentSetting,
        writebackMode,
        isSourceTenantIdFromSubmissionTheRootSourceId,
        sourceId,
        sourceTenantId,
      } = getDataForWriteback();

      if (createPersonSetting.value && createPersonSetting.settingId) {
        settingIdsToRetry.push(createPersonSetting.settingId);
      }

      if (updatePersonSetting.value && updatePersonSetting.settingId) {
        settingIdsToRetry.push(updatePersonSetting.settingId);
      }

      if (uploadDocumentSetting.value && uploadDocumentSetting.settingId) {
        settingIdsToRetry.push(uploadDocumentSetting.settingId);
      }

      const payload: FormsWritebacks.Types.RetryWritebackPayload = {
        settingIds: settingIdsToRetry,
        submissionId: submissionId,
        mode: writebackMode,
        sourceTenantId: isSourceTenantIdFromSubmissionTheRootSourceId ? sourceId : sourceTenantId,
        locationId,
      };

      if (retrySearch) {
        delete payload.sourceTenantId;
      }

      setIsRetryingWriteback(true);
      const response = await retryWriteback(payload);
      setIsRetryingWriteback(false);
      return response;
    },
    [getDataForWriteback, submissionId, locationId, setIsRetryingWriteback, retryWriteback]
  );

  return {
    triggerWritebackRetry,
  };
};
